import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IRolesDAO } from '../../../api/roles.dao';
import { getAllPermissions } from '../../../constants/permission.boilerplate';
import { showNotification } from '../../global/modal/alerts-host/alert-templates';
import { Permission } from '../../../api/roles.dao';
import { IAlertsService } from '../../../infrastructure/alerts';
import { RoleFormDataVM } from './role-form-data.viewmodel';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabled, TRACKING_EVENTS } from '../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';


class RolePermissionFormVM {

    @mobx.observable
    private saving: boolean;

    @mobx.observable
    public isLoading: boolean;

    @mobx.observable
    private roleFormDataVM: RoleFormDataVM;

    @mobx.observable
    public rolePermissions: Set<Permission>;

    @mobx.observable
    public oldPermissions: Set<Permission>;

    @mobx.observable
    public name: string;

    @mobx.observable
    public description: string;

    @mobx.observable
    public permissionsTemplate = [];

    constructor(
        private readonly roleId: string,
        private readonly roleName: string,
        @IoC.Inject private readonly rolesDAO: IRolesDAO = IoC.Container.get(IRolesDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.isLoading = true;
    }

    public isSaving() {
        return this.saving;
    }

    public getFormDataVM(): RoleFormDataVM {
        return this.roleFormDataVM;
    }

    public fetchRolePermissions = async () => {
        const response = await this.rolesDAO.getPermissionsByRoleId({ linkName: this.roleId });
        mobx.runInAction(() => {
            this.rolePermissions = new Set(response.permissions);
            this.oldPermissions = new Set(response.permissions);
            this.permissionsTemplate = getAllPermissions(response.permissions);
            this.isLoading = false;
        });
    };

    public handleSubmit = async (intl): Promise<boolean> => {
        try {
            await this.rolesDAO.updateRolePermissions({
                linkName: this.roleId,
                permissions: Array.from(this.rolePermissions)
            });
            showNotification(
                intl.formatMessage({
                    id: 'oe.permission.permissionUpdated',
                    description: 'Notification about added permission',
                    defaultMessage: 'Permissions updated'
                }));
            this.trackRolePermissionsFormSubmitMixPanel();
        } catch (error) {
            this.onUpdateRoleError(error);
        }
        return true;
    };

    @mobx.action
    private onUpdateRoleError(error): void {
        this.alertsService.alert({
            message: JSON.parse(error.rawResponse).message
        });
    }

    @mobx.action
    public handleTogglePermissionsGroup = (groupId: number) => {
        const changedGroup = this.permissionsTemplate.find(el => el.id === groupId);
        const isGroupSelected = changedGroup.status;
        changedGroup.status = !isGroupSelected;
    };

    @mobx.action
    public handleTogglePermission = (groupId: string, id: number) => {
        const changedPermission = this.permissionsTemplate
            .find(el => el.id === groupId).permissions
            .find(el => el.id === id);

        if (changedPermission.status) {
            this.rolePermissions.delete(changedPermission.id);
        } else {
            this.rolePermissions.add(changedPermission.id);
        }

        this.permissionsTemplate = getAllPermissions(Array.from(this.rolePermissions));
    };

    public toApiPermissionsDTO() {
        return {
            roleId: this.roleId,
            permissions: this.rolePermissions
        };
    }


    public trackRolePermissionsFormSubmitMixPanel = () => {
        const allPermission = this.permissionsTemplate.flatMap(permission => permission.permissions);

        const permissionProps = allPermission.reduce((acc, permission) => {
            const check = (this.oldPermissions.has(permission.id) && this.rolePermissions.has(permission.id))
                || (!this.oldPermissions.has(permission.id) && !this.rolePermissions.has(permission.id))
            ;
            const booleanValue = convertBoolTypeForMixPanel(!check);
            const enabledStatusValue = isTrackingFieldEnabled(this.rolePermissions.has(permission.id));

            acc[`${permission.title}`] = `${booleanValue} - ${enabledStatusValue}`;

            return acc;
        }, {});
        permissionProps['Role Name'] = this.roleName;
        this.trackingService.get().track(TRACKING_EVENTS.MANAGE_PERMISSIONS, { ...permissionProps });
    };

}

export { RolePermissionFormVM };
